/**
 * Plain words for a tenant's plan and the modules it does not include
 * (UAT issue #215).
 *
 * A module the tenant is not licensed for used to vanish from the sidebar and
 * from Jump to without a word: a Data Protection Officer on the Professional
 * plan typed "Detection" and read "No page matches", with nothing to say that
 * Detection (DDR) is a separate module the plan does not include. The sidebar,
 * Jump to and the refusal page now say so, using these labels.
 *
 * Nothing here grants anything: what a tenant may open stays with the licence
 * rows the controller's route gate reads (GET /v1/entitlements/current).
 */

/** Who to ask to add a module. */
export const SUPPORT_EMAIL = 'support@foctta.com';

/** The customer-facing name of a licensed module. */
export const MODULE_LABELS: Readonly<Record<string, string>> = {
  discovery: 'Discovery (DSPM)',
  activity: 'Activity (DAM)',
  ddr: 'Detection (DDR)',
  compliance: 'Compliance',
  core: 'FOCTTA Privacy Core',
  frontier: 'Frontier',
};

/**
 * The plan names of packages/industry-packs/src/bundles.ts (the bundle
 * catalogue, by its `id`). A sku outside the list is shown as written.
 */
const PLAN_LABELS: Readonly<Record<string, string>> = {
  bfsi_enterprise: 'BFSI Enterprise',
  healthcare_enterprise: 'Healthcare Enterprise',
  enterprise: 'Enterprise',
  professional: 'Professional',
  starter: 'Starter',
  msme_light: 'MSME Light',
  consent_only: 'Consent Only',
  auditor_read_only: 'Auditor Read Only',
  discovery_only: 'Discovery',
  activity_only: 'Activity',
  discovery_activity: 'Discovery + Activity',
  complete: 'Complete',
};

/** The plan's name for a sku, or null when the sku is not known. */
export function planLabel(sku: string | null | undefined): string | null {
  if (!sku || sku === 'unknown') return null;
  const known = PLAN_LABELS[sku];
  if (known) return known;
  const words = sku.replace(/[^A-Za-z0-9]+/g, ' ').trim();
  if (!words) return null;
  return words.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The sentence saying a module is not licensed and who adds it, e.g.
 * "Detection (DDR) is not licensed for your organisation (your plan:
 * Professional). To add it, contact support@foctta.com."
 *
 * It says "not licensed", never "your plan does not include": the licence
 * rows decide, and they can differ from the plan's bundle (a module left out
 * at provisioning, a Core module subset or revoke). The plan is named only as
 * the tenant's plan, which is true in every case.
 */
export function notInPlanSentence(moduleLabel: string, plan: string | null): string {
  const planPart = plan ? ` (your plan: ${plan})` : '';
  return `${moduleLabel} is not licensed for your organisation${planPart}. To add it, contact ${SUPPORT_EMAIL}.`;
}

/**
 * When billing is cancelled no module is licensed: the "not licensed" list is
 * not shown and a refused page says this instead. It promises no module back
 * on reinstatement (the review at 66b3b23: on a plan that never had a module,
 * "opens again once reinstated" was false), and says who answers both what
 * reinstating restores and how to add a module.
 */
export const CANCELLED_SENTENCE = `Your organisation's subscription is cancelled. To reinstate it, or to ask which modules it includes, contact ${SUPPORT_EMAIL}.`;
